import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Compass, Quote } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const story = [
  "Levi One est né d'un constat simple, construit au fil de nombreuses années d'expérience dans le domaine des Certificats d'Économies d'Énergie (CEE) et de la rénovation énergétique.",
  "Après plusieurs années en tant que Directeur des Opérations CEE, j'ai pu observer de près les difficultés rencontrées par les entreprises dans leurs projets de rénovation énergétique : un domaine souvent complexe, avec de nombreux dispositifs, des exigences techniques et administratives, et une multitude d'interlocuteurs.",
  "Trop souvent, les entreprises ne disposent pas des bonnes informations au bon moment. Certaines ne connaissent pas les dispositifs auxquels elles peuvent prétendre, d'autres avancent sans accompagnement ou renoncent face à la complexité des démarches.",
  "C'est à partir de ce constat qu'est née l'idée de créer Levi One.",
];

const mission = [
  "Notre cabinet a pour vocation de mettre notre expertise au service des entreprises du secteur tertiaire, en leur apportant un conseil indépendant, une vision claire et un accompagnement de proximité dans leurs projets de rénovation énergétique.",
  "Notre rôle est de vous aider à mieux comprendre les dispositifs existants, à identifier les opportunités adaptées à votre projet, à vous orienter vers les bons partenaires et à vous accompagner dans les différentes étapes de votre démarche.",
];

export default function Who() {
  return (
    <div data-testid="who-page">
      <section className="relative overflow-hidden border-b border-gold/10">
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-24">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-gold mb-4">Qui sommes-nous ?</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-white leading-[1.2]">
              Une expertise née du terrain, une volonté de <span className="gold-text">mieux accompagner les entreprises</span>
            </h1>
          </motion.div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 sm:px-10 py-20" data-testid="who-story">
        {story.map((p, i) => (
          <Reveal key={i} delay={i * 0.06}>
            <p className={`leading-relaxed mb-8 ${
              p.startsWith("C'est à partir")
                ? "text-lg sm:text-xl font-serif font-medium text-white border-l-2 border-gold pl-6"
                : "text-base text-slate-400"
            }`}>
              {p}
            </p>
          </Reveal>
        ))}
        <Reveal>
          <div className="mt-4 flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center shrink-0">
              <Compass size={18} className="text-gold-bright" />
            </span>
            <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-white">Notre mission</h2>
          </div>
        </Reveal>
        {mission.map((p, i) => (
          <Reveal key={i} delay={0.08 + i * 0.06}>
            <p className="mt-6 text-base leading-relaxed text-slate-400">{p}</p>
          </Reveal>
        ))}
      </section>

      <section className="max-w-4xl mx-auto px-6 sm:px-10 pb-20" data-testid="who-conviction">
        <Reveal>
          <div className="relative bg-navy-800/50 border border-gold/20 rounded-3xl p-8 sm:p-12 overflow-hidden">
            <div className="absolute inset-0 hero-glow" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-10 h-10 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center shrink-0">
                  <Quote size={18} className="text-gold-bright" />
                </span>
                <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-white">Notre conviction</h2>
              </div>
              <p className="text-lg sm:text-xl font-serif font-medium text-white leading-relaxed">
                La rénovation énergétique ne devrait pas être un parcours complexe pour les entreprises.
              </p>
              <p className="mt-5 text-base leading-relaxed text-slate-300">
                Notre mission est de rendre ce parcours plus simple, plus lisible et mieux accompagné,
                en mettant notre expérience du terrain et notre connaissance des CEE au service de vos projets.
              </p>
              <p className="mt-5 text-base leading-relaxed text-slate-400">
                Parce qu'au-delà des dispositifs et des démarches, nous pensons qu'un projet réussi repose
                avant tout sur la confiance, la transparence et la qualité de l'accompagnement.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-24 text-center">
        <Reveal>
          <Link
            to="/contact"
            data-testid="who-cta-btn"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-gold to-gold-bright hover:from-gold-bright hover:to-gold text-navy-950 font-semibold px-8 py-4 rounded-full shadow-lg shadow-gold/25 transition-all duration-300 hover:-translate-y-0.5"
          >
            Échanger avec nous <ArrowRight size={18} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
